import apiClient from './apiClient';
import axios from 'axios';

/**
 * Host Shared 虚拟主机管理服务
 *
 * 处理虚拟主机相关的所有API调用
 * - 用户端路由: /api/user/host_shared/*
 * - 管理员路由: /api/admin/host_shared/*
 */
class HostSharedService {
    constructor() {
        // 缓存JWT token和API URL
        this.jwtCache = {};
    }

    /**
     * 获取文件管理 JWT Token
     */
    async getFileJWT(vhostId, expiresIn = 3600) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/files/jwt`, {
                expires_in: expiresIn
            });

            if (response.data.success) {
                const data = response.data.data;
                // 缓存token
                this.jwtCache[vhostId] = {
                    token: data.token,
                    api_url: data.api_url,
                    vhost: data.vhost,
                    expires_at: data.expires_at
                };
                return response.data;
            }
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 使用JWT直接访问CDN API
     */
    async callCdnApi(vhostId, action, params = {}, method = 'GET') {
        try {
            // 检查是否有缓存的token，如果没有或已过期，则重新获取
            let jwtData = this.jwtCache[vhostId];
            const now = Math.floor(Date.now() / 1000);

            if (!jwtData || (jwtData.expires_at && jwtData.expires_at < now + 60)) {
                // Token不存在或将在60秒内过期，重新获取
                console.log('[callCdnApi] 获取新的JWT token for vhostId:', vhostId);
                const result = await this.getFileJWT(vhostId);
                if (!result.success) {
                    console.error('[callCdnApi] JWT获取失败:', result.error);
                    return result;
                }
                jwtData = this.jwtCache[vhostId];
            }

            // 构建请求URL - api_url可能已经包含/api/index.php，需要处理
            let baseUrl = jwtData.api_url;
            // 如果api_url不包含index.php，则添加
            if (!baseUrl.includes('index.php')) {
                baseUrl = baseUrl.endsWith('/') ? baseUrl + 'api/index.php' : baseUrl + '/api/index.php';
            }

            const config = {
                headers: {
                    'Authorization': `Bearer ${jwtData.token}`
                },
                // 控制器和 action 放在 URL query string
                params: {
                    c: 'vhost_api',
                    a: action,
                    vhost: jwtData.vhost
                }
            };

            console.log('[callCdnApi] 请求详情:', {
                method,
                url: baseUrl,
                action,
                hasAuth: !!config.headers.Authorization,
                authHeader: config.headers.Authorization,
                queryParams: config.params,
                bodyParams: params
            });

            let response;
            if (method === 'GET') {
                // GET: 所有参数都放 URL
                config.params = { ...config.params, ...params };
                response = await axios.get(baseUrl, config);
            } else {
                // POST: c/a/vhost 在 URL，其他数据在 body
                // 使用 application/x-www-form-urlencoded 格式
                const formData = new URLSearchParams();
                for (const key in params) {
                    if (Array.isArray(params[key])) {
                        // 数组参数：每个元素单独添加
                        params[key].forEach(value => {
                            formData.append(key + '[]', value);
                        });
                    } else {
                        formData.append(key, params[key]);
                    }
                }
                response = await axios.post(baseUrl, formData, config);
            }

            console.log('[callCdnApi] 响应:', response.data);

            // 处理返回数据
            if (response.data && response.data.code) {
                // 2xx 状态码都算成功
                if (response.data.code >= 200 && response.data.code < 300) {
                    return { success: true, data: response.data.data, message: response.data.message };
                } else {
                    return { success: false, error: response.data.message || '操作失败' };
                }
            }

            return { success: true, data: response.data };
        } catch (error) {
            console.error('[callCdnApi] 请求失败:', error);
            return { success: false, error: error.response?.data?.message || error.message };
        }
    }
    /** 节点文件管理入口(JWT 直连):返回 {baseUrl, token, vhost} */
    async getFileApi(vhostId) {
        let jwtData = this.jwtCache[vhostId];
        const now = Math.floor(Date.now() / 1000);
        if (!jwtData || (jwtData.expires_at && jwtData.expires_at < now + 60)) {
            const result = await this.getFileJWT(vhostId);
            if (!result.success) {
                throw new Error(result.error || 'JWT');
            }
            jwtData = this.jwtCache[vhostId];
        }
        let baseUrl = jwtData.api_url;
        if (!baseUrl.includes('index.php')) {
            baseUrl = baseUrl.endsWith('/') ? baseUrl + 'api/index.php' : baseUrl + '/api/index.php';
        }
        return { baseUrl, token: jwtData.token, vhost: jwtData.vhost };
    }

    /** 站点内文件的直链下载地址(token 在 URL 里,同 FileManager 下载) */
    async fileDownloadUrl(vhostId, path) {
        const { baseUrl, token, vhost } = await this.getFileApi(vhostId);
        return `${baseUrl}?c=vhost_api&a=file_download&vhost=${encodeURIComponent(vhost)}&file=${encodeURIComponent(path)}&token=${encodeURIComponent(token)}`;
    }

    /** 今天的站点日志下载地址(kind: access | error;节点 file_sitelog,令牌同文件下载) */
    async siteLogDownloadUrl(vhostId, kind) {
        const { baseUrl, token, vhost } = await this.getFileApi(vhostId);
        return `${baseUrl}?c=vhost_api&a=file_sitelog&vhost=${encodeURIComponent(vhost)}&kind=${encodeURIComponent(kind)}&token=${encodeURIComponent(token)}`;
    }

    /** 站点日志最近 N 行:{kind, lines, status, path} */
    async getSiteLogs(vhostId, params) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/logs`, { params });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }

    /** 异地备份列表 {enabled, keep, backups[], restore} */
    async getOffsiteBackups(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/offsite-backups`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }

    /** 从异地备份恢复 {files, db} */
    async restoreOffsiteBackup(vhostId, backupId, { files, db }) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/offsite-backups/${backupId}/restore`, { files, db, confirm: true });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }

    async getOffsiteRestore(vhostId, restoreId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/offsite-restores/${restoreId}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }

    /** 上传文件到站点目录(节点 vhost_api file_upload,multipart: file + path) */
    async uploadFile(vhostId, path, file) {
        try {
            const { baseUrl, token, vhost } = await this.getFileApi(vhostId);
            const form = new FormData();
            form.append('path', path || '/');
            form.append('file', file);
            const response = await axios.post(baseUrl, form, {
                headers: { Authorization: `Bearer ${token}` },
                params: { c: 'vhost_api', a: 'file_upload', vhost }
            });
            const d = response.data || {};
            if (d.code >= 200 && d.code < 300) {
                return { success: true, data: d.data };
            }
            return { success: false, error: d.message || d.msg || 'Upload failed' };
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.message };
        }
    }

    // ==========================================
    // 用户端 - 虚拟主机管理
    // ==========================================

    /**
     * 获取用户的虚拟主机列表
     */
    async getVhosts() {
        try {
            const response = await apiClient.get('/api/user/host_shared/vhosts');
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 已付款、开通中的订单（资源不足排队）
     */
    async getPending() {
        try {
            const response = await apiClient.get('/api/user/host_shared/pending');
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取用户的虚拟主机列表（别名）
     */
    async getMyVhosts() {
        return this.getVhosts();
    }

    /**
     * 获取虚拟主机详情
     */
    async getVhost(id) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${id}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除虚拟主机（最后一台被删 → 订阅结束，不退款）
     */
    async deleteVhost(id) {
        try {
            const response = await apiClient.delete(`/api/user/host_shared/vhosts/${id}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取虚拟主机配额信息
     */
    async getVhostQuota(id) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${id}/quota`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取用户的所有SSL证书
     */
    async getSslCerts() {
        try {
            const response = await apiClient.get('/api/user/ssl/certificates');
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // 域名管理
    // ==========================================

    /**
     * 获取虚拟主机的域名列表
     */
    async getDomains(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/domains`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 添加域名
     */
    async addDomain(vhostId, data) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/domains`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新域名
     */
    async updateDomain(vhostId, domainId, data) {
        try {
            const response = await apiClient.put(`/api/user/host_shared/vhosts/${vhostId}/domains/${domainId}`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除域名
     */
    async deleteDomain(vhostId, domainId) {
        try {
            const response = await apiClient.delete(`/api/user/host_shared/vhosts/${vhostId}/domains/${domainId}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // 计划任务管理
    // ==========================================

    /**
     * 获取计划任务列表
     */
    async getCrons(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/crons`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 创建计划任务
     */
    async createCron(vhostId, data) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/crons`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新计划任务
     */
    async updateCron(vhostId, cronId, data) {
        try {
            const response = await apiClient.put(`/api/user/host_shared/vhosts/${vhostId}/crons/${cronId}`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新计划任务状态
     */
    async updateCronStatus(vhostId, cronId, status) {
        try {
            const response = await apiClient.put(
                `/api/user/host_shared/vhosts/${vhostId}/crons/${cronId}/status`,
                { status }
            );
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除计划任务
     */
    async deleteCron(vhostId, cronId) {
        try {
            const response = await apiClient.delete(`/api/user/host_shared/vhosts/${vhostId}/crons/${cronId}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // 备份管理
    // ==========================================

    /**
     * 获取备份列表
     */
    async getBackups(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/backups`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 修改 FTP 密码(= 站点面板登录密码)
     */
    async changeFtpPassword(vhostId, password) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/ftp/password`, { password });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 修改数据库密码
     */
    async changeDatabasePassword(vhostId, password) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/database/password`, { password });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 重启虚拟主机
     */
    async reloadVhost(vhostId) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/reload`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取PHP版本列表
     */
    async getPhpVersions(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/php-versions`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取PHP配置
     */
    async getPhpConfig(vhostName) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/php-config`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 设置PHP版本
     */
    async setPhpVersion(vhostName, version) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/php-version`, { version });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取缓存列表
     */
    async getCacheList(vhostName) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/cache`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 添加缓存规则
     */
    async addCache(vhostName, config) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/cache`, config);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除缓存规则
     */
    async deleteCache(vhostName, cacheId) {
        try {
            const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/cache/${cacheId}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 切换缓存控制状态
     */
    async toggleCache(vhostName, enabled) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/cache/toggle`, {
                enabled
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取Gzip配置
     */
    async getGzip(vhostName) {
        return this.getGzipFull(vhostName);
    }

    /**
     * 启用Gzip
     */
    async enableGzip(vhostName, pattern) {
        return this.setGzip(vhostName, pattern);
    }

    /**
     * 禁用Gzip
     */
    async disableGzip(vhostName) {
        return this.deleteGzip(vhostName);
    }

    /**
     * 创建备份
     */
    async createBackup(vhostId) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/backups`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // SSL证书管理
    // ==========================================

    /**
     * 获取虚拟主机的SSL证书列表
     */
    async getSslCertificates(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/ssl`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 部署SSL证书
     * @param {number} vhostId - 虚拟主机ID
     * @param {string} domain - 域名字符串
     * @param {number} certId - 证书ID
     */
    async deploySsl(vhostId, domain, certId) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/ssl/deploy`, {
                ssl_id: certId,
                domain: domain
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 移除SSL证书
     */
    async removeSsl(vhostId, domain) {
        try {
            const response = await apiClient.delete(`/api/user/host_shared/vhosts/${vhostId}/ssl/remove`, {
                data: { domain }
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * Let's Encrypt 免费证书:状态 / 申请 / 停用
     */
    async getLetsEncrypt(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/ssl/letsencrypt`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    async requestLetsEncrypt(vhostId, domains, dryRun = false) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/ssl/letsencrypt`, {
                domains,
                dry_run: dryRun
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    async disableLetsEncrypt(vhostId, remove = false) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/ssl/letsencrypt/disable`, { remove });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 强制HTTPS
     */
    async forceHttps(vhostId, domain, enable) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/ssl/force-https`, {
                domain,
                enable
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新域名的强制HTTPS设置（别名方法）
     */
    async updateDomainForceHttps(vhostId, domainId, enabled) {
        // 注意：domainId 在VhostDetail中传入的是domain的ID
        // 但后端API需要的是domain字符串
        // 前端调用时需要传domain字符串而不是ID
        return this.forceHttps(vhostId, domainId, enabled);
    }

    // ==========================================
    // 文件管理
    // ==========================================

    /**
     * 获取文件列表 (直接使用JWT访问CDN API)
     */
    async getFiles(vhostId, path = '/') {
        return this.callCdnApi(vhostId, 'file_list', { path }, 'GET');
    }

    /**
     * 创建文件夹 (直接使用JWT访问CDN API)
     */
    async createFolder(vhostId, path) {
        return this.callCdnApi(vhostId, 'dir_create', { path }, 'POST');
    }

    /**
     * 创建文件 (直接使用JWT访问CDN API)
     */
    async createFile(vhostId, file, content = '') {
        return this.callCdnApi(vhostId, 'file_write', { file, content }, 'POST');
    }

    /**
     * 读取文件内容 (直接使用JWT访问CDN API)
     */
    async readFile(vhostId, file) {
        return this.callCdnApi(vhostId, 'file_read', { file }, 'GET');
    }

    /**
     * 写入文件内容 (直接使用JWT访问CDN API)
     */
    async writeFile(vhostId, file, content) {
        return this.callCdnApi(vhostId, 'file_write', { file, content }, 'POST');
    }

    /**
     * 重命名文件 (直接使用JWT访问CDN API)
     */
    async renameFile(vhostId, oldname, newname) {
        return this.callCdnApi(vhostId, 'file_rename', { oldname, newname }, 'POST');
    }

    /**
     * 移动文件或目录 (直接使用JWT访问CDN API)
     */
    async moveFile(vhostId, source, destination) {
        return this.callCdnApi(vhostId, 'file_move', { source, destination }, 'POST');
    }

    /**
     * 复制文件或目录 (直接使用JWT访问CDN API)
     */
    async copyFile(vhostId, source, destination) {
        return this.callCdnApi(vhostId, 'file_copy', { source, destination }, 'POST');
    }

    /**
     * 修改文件权限 (直接使用JWT访问CDN API)
     */
    async chmodFile(vhostId, file, mode) {
        return this.callCdnApi(vhostId, 'file_chmod', { file, mode }, 'POST');
    }

    /**
     * 压缩文件或目录 (直接使用JWT访问CDN API)
     */
    async compressFiles(vhostId, files, destination, password = '') {
        return this.callCdnApi(vhostId, 'file_compress', { files, destination, password }, 'POST');
    }

    /**
     * 解压文件 (直接使用JWT访问CDN API)
     */
    async decompressFile(vhostId, file, destination = '', password = '') {
        return this.callCdnApi(vhostId, 'file_decompress', { file, destination, password }, 'POST');
    }

    /**
     * 获取文件访问控制列表
     */
    async getFileAccess(vhostName) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/file-access`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 设置文件访问控制
     */
    async setFileAccess(vhostName, payload) {
        try {
            const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/file-access`, payload);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除文件 (直接使用JWT访问CDN API)
     */
    async deleteFile(vhostId, file) {
        return this.callCdnApi(vhostId, 'file_delete', { file }, 'POST');
    }

    /**
     * 批量删除文件 (直接使用JWT访问CDN API)
     */
    async batchDeleteFiles(vhostId, files) {
        return this.callCdnApi(vhostId, 'file_delete', { files }, 'POST');
    }

    // ==========================================
    // 安全设置
    // ==========================================

    /**
     * 获取安全设置
     */
    async getSecuritySettings(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/security`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // 性能优化
    // ==========================================

    /**
     * 获取性能设置
     */
    async getPerformanceSettings(vhostId) {
        try {
            const response = await apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/performance`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // 管理员端 - 节点管理
    // ==========================================

    /**
     * 获取节点列表（管理员）
     */
    async getNodes() {
        try {
            const response = await apiClient.get('/api/admin/host_shared/nodes');
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 创建节点（管理员）
     */
    async createNode(data) {
        try {
            const response = await apiClient.post('/api/admin/host_shared/nodes', data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新节点（管理员）
     */
    async updateNode(id, data) {
        try {
            const response = await apiClient.put(`/api/admin/host_shared/nodes/${id}`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除节点（管理员）
     */
    async deleteNode(id) {
        try {
            const response = await apiClient.delete(`/api/admin/host_shared/nodes/${id}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 测试节点连接（管理员）
     */
    async testNode(id) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/nodes/${id}/test`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新节点状态（管理员）
     */
    async updateNodeStatus(id, status) {
        try {
            const response = await apiClient.put(`/api/admin/host_shared/nodes/${id}/status`, { status });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // 管理员端 - 虚拟主机管理
    // ==========================================

    /**
     * 获取虚拟主机列表（管理员）
     */
    async getAdminVhosts(params = {}) {
        try {
            const response = await apiClient.get('/api/admin/host_shared/vhosts', { params });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取虚拟主机详情（管理员）
     */
    async getAdminVhost(id) {
        try {
            const response = await apiClient.get(`/api/admin/host_shared/vhosts/${id}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 创建虚拟主机（管理员）
     */
    async createAdminVhost(data) {
        try {
            const response = await apiClient.post('/api/admin/host_shared/vhosts', data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新虚拟主机（管理员）
     */
    async updateAdminVhost(id, data) {
        try {
            const response = await apiClient.put(`/api/admin/host_shared/vhosts/${id}`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除虚拟主机（管理员）
     */
    async deleteAdminVhost(id) {
        try {
            const response = await apiClient.delete(`/api/admin/host_shared/vhosts/${id}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 修改虚拟主机密码（管理员）
     */
    async changeAdminVhostPassword(id, password) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/change-password`, { password });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 更新虚拟主机状态（管理员）
     */
    async updateVhostStatus(id, status) {
        try {
            const response = await apiClient.put(`/api/admin/host_shared/vhosts/${id}/status`, { status });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // 别名，保持命名一致
    async updateAdminVhostStatus(id, status) {
        return this.updateVhostStatus(id, status);
    }

    /**
     * 更新虚拟主机配额（管理员）
     */
    async updateVhostQuota(id, data) {
        try {
            const response = await apiClient.put(`/api/admin/host_shared/vhosts/${id}/quota`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // 别名，保持命名一致
    async updateAdminVhostQuota(id, data) {
        return this.updateVhostQuota(id, data);
    }

    /**
     * 获取用户列表（用于下拉选择器）
     */
    async getUsers(keyword = '') {
        try {
            const response = await apiClient.get('/api/admin/users', {
                params: { search: keyword }
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    // ==========================================
    // 管理员端 - 虚拟主机生命周期管理
    // ==========================================

    /**
     * 暂停虚拟主机（管理员）
     */
    async suspendVhost(id, reason = '') {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/suspend`, { reason });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 恢复虚拟主机（管理员）
     */
    async resumeVhost(id) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/resume`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 终止虚拟主机（管理员）
     */
    async terminateVhost(id, hardDelete = false) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/terminate`, { hard_delete: hardDelete });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 激活虚拟主机（管理员）
     */
    async activateVhost(id) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/activate`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 转移虚拟主机所有权（管理员）
     */
    async changeVhostOwner(id, newUserId) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/change-owner`, { new_user_id: newUserId });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 强制同步虚拟主机到节点（管理员）
     */
    async forceSyncVhost(id) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/force-sync`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 延长虚拟主机到期时间（管理员）
     */
    async extendVhost(id, days, free = false) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/extend`, { days, free });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取PHP详细配置（扩展和参数）
     */
    async getPhpDetail(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/php-detail`);
        return response.data;
    }

    /**
     * 设置PHP详细配置（扩展和参数）
     */
    async setPhpDetail(vhostName, ext, config) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/php-detail`, {
            ext,
            config
        });
        return response.data;
    }

    /**
     * 重置PHP配置到默认值
     */
    async resetPhpDetail(vhostName) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/php-detail/reset`);
        return response.data;
    }

    // ==================== 安全设置 ====================

    /**
     * 获取IP封禁列表
     */
    async getBanIP(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/banip`);
        return response.data;
    }

    /**
     * 添加IP封禁
     */
    async addBanIP(vhostName, ip) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/banip`, { ip });
        return response.data;
    }

    /**
     * 删除IP封禁(按值:IP 或 CIDR,不用列表位置)
     */
    async deleteBanIP(vhostName, ip) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/banip`, { params: { ip } });
        return response.data;
    }

    /**
     * 开关IP黑名单
     */
    async toggleBanIP(vhostName, enabled) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/banip/toggle`, { enabled: !!enabled });
        return response.data;
    }

    /**
     * 获取防CC配置
     */
    async getAntiCC(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/anticc`);
        return response.data;
    }

    /**
     * 设置防CC配置
     */
    async setAntiCC(vhostName, config) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/anticc`, config);
        return response.data;
    }

    /**
     * 删除防CC配置
     */
    async deleteAntiCC(vhostName) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/anticc`);
        return response.data;
    }

    /**
     * 获取防SQL注入配置
     */
    async getAntiSQL(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/antisql`);
        return response.data;
    }

    /**
     * 启用防SQL注入
     */
    async enableAntiSQL(vhostName) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/antisql/enable`);
        return response.data;
    }

    /**
     * 禁用防SQL注入
     */
    async disableAntiSQL(vhostName) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/antisql/disable`);
        return response.data;
    }

    /**
     * 添加防SQL注入规则
     */
    async addAntiSQLRule(vhostName, rule) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/antisql/rule`, rule);
        return response.data;
    }

    /**
     * 删除防SQL注入规则
     */
    async deleteAntiSQLRule(vhostName, ruleId) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/antisql/rule/${ruleId}`);
        return response.data;
    }

    // ==========================================
    // 防木马上传管理
    // ==========================================

    /**
     * 获取防木马上传列表
     */
    async getAntiUpload(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/antiupload`);
        return response.data;
    }

    /**
     * 添加防木马上传规则
     */
    async addAntiUpload(vhostName, filename) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/antiupload`, { filename });
        return response.data;
    }

    /**
     * 删除防木马上传规则
     */
    async deleteAntiUpload(vhostName, id) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/antiupload/${id}`);
        return response.data;
    }

    /**
     * 切换防木马上传状态
     */
    async toggleAntiUpload(vhostName, enabled) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/antiupload/toggle`, { enabled });
        return response.data;
    }

    // ==========================================
    // 防盗链管理 (新版完整)
    // ==========================================

    /**
     * 获取防盗链设置
     */
    async getHotlink(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/hotlink`);
        return response.data;
    }

    /**
     * 启用/禁用防盗链
     */
    async toggleHotlink(vhostName, enabled) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/hotlink/toggle`, { enabled });
        return response.data;
    }

    /**
     * 添加防盗链规则
     */
    async addHotlink(vhostName, path, referer = '', redirect = '') {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/hotlink`, {
            path,
            referer,
            redirect
        });
        return response.data;
    }

    /**
     * 删除防盗链规则
     */
    async deleteHotlink(vhostName, id) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/hotlink/${id}`);
        return response.data;
    }

    // ==========================================
    // Gzip压缩管理 (增强版)
    // ==========================================

    /**
     * 获取Gzip压缩设置 (完整版,包含pattern)
     */
    async getGzipFull(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/gzip-full`);
        return response.data;
    }

    /**
     * 设置Gzip压缩
     */
    async setGzip(vhostName, pattern) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/gzip-set`, { pattern });
        return response.data;
    }

    /**
     * 删除Gzip压缩设置
     */
    async deleteGzip(vhostName) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/gzip-delete`);
        return response.data;
    }

    // ==========================================
    // SSL证书管理 (单域名)
    // ==========================================

    /**
     * 获取单域名SSL证书
     */
    async getDomainSsl(vhostName, domain) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/domains/${domain}/ssl`);
        return response.data;
    }

    /**
     * 设置单域名SSL证书
     */
    async setDomainSsl(vhostName, domain, certificate, certificate_key) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/domains/${domain}/ssl`, {
            certificate,
            certificate_key
        });
        return response.data;
    }

    /**
     * 设置单域名SSL重定向
     */
    async setDomainSslRewrite(vhostName, domain, enabled) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/domains/${domain}/ssl-rewrite`, {
            enabled
        });
        return response.data;
    }

    /**
     * 获取全局SSL设置
     */
    async getSslFull(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/ssl-full`);
        return response.data;
    }

    /**
     * 设置全局SSL证书
     */
    async setSslFull(vhostName, certificate, certificate_key) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/ssl-full`, {
            certificate,
            certificate_key
        });
        return response.data;
    }

    /**
     * 设置SSL重定向
     */
    async setSslRewrite(vhostName, enabled) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/ssl-rewrite`, { enabled });
        return response.data;
    }

    /**
     * 设置HTTP2
     */
    async setHttp2(vhostName, enabled) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/http2`, { enabled });
        return response.data;
    }

    // ==========================================
    // FTP管理
    // ==========================================

    /**
     * 获取FTP设置
     */
    async getFtpConfig(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/ftp-config`);
        return response.data;
    }

    /**
     * 设置FTP配置
     */
    async setFtpConfig(vhostName, enabled, ftp_dir = '') {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/ftp-config`, {
            enabled,
            ftp_dir
        });
        return response.data;
    }

    // ==========================================
    // 数据库管理
    // ==========================================

    /**
     * 获取数据库信息
     */
    async getDatabaseInfo(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/database-info`);
        return response.data;
    }

    /**
     * 远程导入数据库
     */
    async remoteImportDatabase(vhostName, { rehost, reuser, repasswd, redate, passwd }) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/database/remote-import`, {
            rehost, reuser, repasswd, redate, passwd
        });
        return response.data;
    }

    // ==========================================
    // 伪静态管理 (增强版)
    // ==========================================

    /**
     * 获取伪静态路径列表
     */
    async getRewritePaths(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/rewrite/paths`);
        return response.data;
    }

    /**
     * 获取伪静态规则内容
     */
    async getRewriteContent(vhostName, id) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/rewrite/${id}`);
        return response.data;
    }

    /**
     * 设置伪静态规则
     */
    async setRewriteContent(vhostName, id, content) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/rewrite/${id}`, {
            content
        });
        return response.data;
    }

    /**
     * 删除伪静态规则
     */
    async deleteRewriteContent(vhostName, id) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/rewrite/${id}`);
        return response.data;
    }

    /**
     * 获取伪静态模板列表
     */
    async getRewriteRules(vhostName) {
        const response = await apiClient.get('/api/user/host_shared/rewrite/rules', {
            params: { vhost: vhostName }
        });
        return response.data;
    }

    /**
     * 获取伪静态模板内容
     */
    async getRewriteRuleContent(vhostName, name) {
        const response = await apiClient.get(`/api/user/host_shared/rewrite/rules/${name}`, {
            params: { vhost: vhostName }
        });
        return response.data;
    }

    // ==========================================
    // 主机管理
    // ==========================================

    /**
     * 重启虚拟主机进程
     */
    async rebootProcess(vhostName) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/reboot`);
        return response.data;
    }

    /**
     * 获取域名详细信息
     */
    async getDomainInfo(vhostName, domain) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/domains/${domain}/info`);
        return response.data;
    }

    /**
     * 获取虚拟主机详细信息
     */
    async getVhostInfo(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/info`);
        return response.data;
    }

    // ==========================================
    // HTTP认证管理
    // ==========================================

    /**
     * 获取HTTP认证列表
     */
    async getHttpAuth(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/httpauth`);
        return response.data;
    }

    /**
     * 添加HTTP认证
     */
    async addHttpAuth(vhostName, user, passwd) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/httpauth`, {
            user,
            passwd
        });
        return response.data;
    }

    /**
     * 删除HTTP认证
     */
    async deleteHttpAuth(vhostName, user) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/httpauth/${user}`);
        return response.data;
    }

    // ==========================================
    // 默认首页管理
    // ==========================================

    /**
     * 获取默认首页列表
     */
    async getIndexFile(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/indexfile`);
        return response.data;
    }

    /**
     * 添加默认首页
     */
    async addIndexFile(vhostName, file) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/indexfile`, { file });
        return response.data;
    }

    /**
     * 删除默认首页
     */
    async deleteIndexFile(vhostName, file) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/indexfile/${encodeURIComponent(file)}`);
        return response.data;
    }

    // ==========================================
    // 错误页面管理
    // ==========================================

    /**
     * 获取错误页面列表
     */
    async getErrorPage(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/errorpage`);
        return response.data;
    }

    /**
     * 添加错误页面
     */
    async addErrorPage(vhostName, code, file) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/errorpage`, {
            code,
            file
        });
        return response.data;
    }

    /**
     * 删除错误页面
     */
    async deleteErrorPage(vhostName, code) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/errorpage/${code}`);
        return response.data;
    }

    // ==========================================
    // MIME类型管理
    // ==========================================

    /**
     * 获取MIME类型列表
     */
    async getMime(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/mime`);
        return response.data;
    }

    /**
     * 添加MIME类型
     */
    async addMime(vhostName, payload) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/mime`, payload);
        return response.data;
    }

    /**
     * 删除MIME类型
     */
    async deleteMime(vhostName, name) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/mime/${encodeURIComponent(name)}`);
        return response.data;
    }

    // ==========================================
    // 防盗链管理 (旧版 - 保持兼容)
    // ==========================================





    // ==========================================
    // SSI管理
    // ==========================================




    // ==========================================
    // One-click apps (app store, by vhost id). Errors come back as {success:false, error, error_code, details}.
    // ==========================================

    async _appsCall(fn) {
        try {
            const response = await fn({ skipGlobalErrorToast: true });
            return response.data;
        } catch (e) {
            const body = e.response?.data;
            if (body && typeof body === 'object') return { success: false, ...body };
            return { success: false, error: e.message };
        }
    }

    getAppCatalog(vhostId) {
        return this._appsCall((cfg) => apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/apps/catalog`, cfg));
    }

    getInstalledApps(vhostId) {
        return this._appsCall((cfg) => apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/apps`, cfg));
    }

    installApp(vhostId, params) {
        return this._appsCall((cfg) => apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/apps`, params, cfg));
    }

    getAppJob(vhostId, appId) {
        return this._appsCall((cfg) => apiClient.get(`/api/user/host_shared/vhosts/${vhostId}/apps/${appId}/job`, cfg));
    }

    updateApp(vhostId, appId) {
        return this._appsCall((cfg) => apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/apps/${appId}/update`, {}, cfg));
    }

    uninstallApp(vhostId, appId, slug) {
        return this._appsCall((cfg) => apiClient.post(`/api/user/host_shared/vhosts/${vhostId}/apps/${appId}/uninstall`, { confirm: slug }, cfg));
    }

    // ==========================================
    // Web应用管理
    // ==========================================

    /**
     * 获取Web应用列表
     */
    async getWebapp(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/webapp`);
        return response.data;
    }

    /**
     * 卸载Web应用
     */
    async uninstallWebapp(vhostName, id) {
        const response = await apiClient.delete(`/api/user/host_shared/vhosts/name/${vhostName}/webapp/${id}`);
        return response.data;
    }

    /**
     * 获取Webapp可用域名信息
     */
    async getWebappDomainInfo(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/webapp/domain-info`);
        return response.data;
    }

    /**
     * 下载Webapp安装包
     */
    async downloadWebapp(vhostName, appid) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/webapp/download`, { appid });
        return response.data;
    }

    /**
     * 查询Webapp下载进度
     */
    async getWebappDownloadStatus(vhostName, appid) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/webapp/download-status`, { params: { appid } });
        return response.data;
    }

    /**
     * 安装Webapp
     */
    async installWebapp(vhostName, params) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/webapp/install`, params);
        return response.data;
    }

    /**
     * 查询Webapp安装/卸载进度
     */
    async getWebappInstallStatus(vhostName, phyDir, op = 'install') {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/webapp/install-status`, { params: { phy_dir: phyDir, op } });
        return response.data;
    }

    /**
     * 标记Webapp安装完成
     */
    async completeWebappInstall(vhostName, id) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/webapp/install-complete`, { id });
        return response.data;
    }

    // ==========================================
    // Admin Webapp管理
    // ==========================================

    /**
     * [Admin] 获取虚拟主机已安装的Webapp列表
     */
    async adminGetWebapp(vhostId) {
        const response = await apiClient.get(`/api/admin/host_shared/vhosts/${vhostId}/webapp`);
        return response.data;
    }

    /**
     * [Admin] 获取Webapp可用域名信息
     */
    async adminGetWebappDomainInfo(vhostId) {
        const response = await apiClient.get(`/api/admin/host_shared/vhosts/${vhostId}/webapp/domain-info`);
        return response.data;
    }

    /**
     * [Admin] 下载Webapp安装包
     */
    async adminDownloadWebapp(vhostId, appid) {
        const response = await apiClient.post(`/api/admin/host_shared/vhosts/${vhostId}/webapp/download`, { appid });
        return response.data;
    }

    /**
     * [Admin] 查询Webapp下载进度
     */
    async adminGetWebappDownloadStatus(vhostId, appid) {
        const response = await apiClient.get(`/api/admin/host_shared/vhosts/${vhostId}/webapp/download-status`, { params: { appid } });
        return response.data;
    }

    /**
     * [Admin] 安装Webapp
     */
    async adminInstallWebapp(vhostId, params) {
        const response = await apiClient.post(`/api/admin/host_shared/vhosts/${vhostId}/webapp/install`, params);
        return response.data;
    }

    /**
     * [Admin] 查询Webapp安装/卸载进度
     */
    async adminGetWebappInstallStatus(vhostId, phyDir, op = 'install') {
        const response = await apiClient.get(`/api/admin/host_shared/vhosts/${vhostId}/webapp/install-status`, { params: { phy_dir: phyDir, op } });
        return response.data;
    }

    /**
     * [Admin] 标记Webapp安装完成
     */
    async adminCompleteWebappInstall(vhostId, id) {
        const response = await apiClient.post(`/api/admin/host_shared/vhosts/${vhostId}/webapp/install-complete`, { id });
        return response.data;
    }

    /**
     * [Admin] 卸载Webapp
     */
    async adminUninstallWebapp(vhostId, appId) {
        const response = await apiClient.delete(`/api/admin/host_shared/vhosts/${vhostId}/webapp/${appId}`);
        return response.data;
    }

    // ==========================================
    // Webapp应用商店管理 (Admin)
    // ==========================================

    async getWebappApps(category) {
        const params = category ? { category } : {};
        const response = await apiClient.get('/api/admin/host_shared/webapp-apps', { params });
        return response.data;
    }

    async createWebappApp(data) {
        const response = await apiClient.post('/api/admin/host_shared/webapp-apps', data);
        return response.data;
    }

    async updateWebappApp(id, data) {
        const response = await apiClient.put(`/api/admin/host_shared/webapp-apps/${id}`, data);
        return response.data;
    }

    async deleteWebappApp(id) {
        const response = await apiClient.delete(`/api/admin/host_shared/webapp-apps/${id}`);
        return response.data;
    }

    async toggleWebappApp(id) {
        const response = await apiClient.post(`/api/admin/host_shared/webapp-apps/${id}/toggle`);
        return response.data;
    }

    // 公共端点 - 获取可用应用列表（无需认证）
    async getPublicWebappList() {
        const response = await apiClient.get('/api/public/webapp/list');
        return response.data;
    }

    // ==========================================
    // CDN管理
    // ==========================================





    // ==========================================
    // 速度限制管理
    // ==========================================







    // ==========================================
    // 连接数限制管理
    // ==========================================



    // ==========================================
    // 模板环境变量管理
    // ==========================================



    // ==========================================
    // 能力检测
    // ==========================================


    // ==========================================
    // Shell工具
    // ==========================================

    /**
     * wget下载文件
     */
    async shellWget(vhostName, url, dir, filename, continueDownload = false) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/shell/wget`, {
            url,
            dir,
            filename,
            continue: continueDownload
        });
        return response.data;
    }

    /**
     * 导出数据库
     */
    async exportDatabase(vhostName, password) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/database/export`, {
            password
        });
        return response.data;
    }

    /**
     * 导入数据库
     */
    async importDatabase(vhostName, password, file) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/database/import`, {
            password,
            file
        });
        return response.data;
    }

    /**
     * 备份网站
     */
    async backupWeb(vhostName, backupLogs = true, password = '') {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/backup/web`, {
            backup_logs: backupLogs,
            password
        });
        return response.data;
    }

    /**
     * 恢复网站
     */
    async restoreWeb(vhostName, file, password = '', overwrite = false) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/restore/web`, {
            file,
            password,
            overwrite
        });
        return response.data;
    }

    /**
     * 查询Shell命令执行状态
     */
    async queryShell(vhostName, session) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/shell/query`, {
            params: { session }
        });
        return response.data;
    }

    // ==========================================
    // 流量统计
    // ==========================================

    /**
     * 查看流量统计
     */
    async viewFlow(vhostName, type = 'day') {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/flow/view`, {
            params: { type }
        });
        return response.data;
    }

    // ==========================================
    // 缓存清理
    // ==========================================

    /**
     * 获取可清理的域名列表
     */
    async getCacheDomains(vhostName) {
        const response = await apiClient.get(`/api/user/host_shared/vhosts/name/${vhostName}/cache/domains`);
        return response.data;
    }

    /**
     * 清除缓存
     */
    async cleanCacheByUrl(vhostName, url) {
        const response = await apiClient.post(`/api/user/host_shared/vhosts/name/${vhostName}/cache/clean-url`, { url });
        return response.data;
    }

    // ==========================================
    // URL重写管理
    // ==========================================

    /**
     * 获取URL重写规则列表 (别名，使用增强版方法)
     */
    async getRewriteList(vhostName) {
        return this.getRewritePaths(vhostName);
    }

    /**
     * 添加URL重写规则 (已废弃 - 使用 setRewriteContent 代替)
     */
    async addRewrite(vhostName, config) {
        console.warn('addRewrite is deprecated, use setRewriteContent instead');
        return this.setRewriteContent(vhostName, config.id, config.content);
    }

    /**
     * 删除URL重写规则 (别名，使用增强版方法)
     */
    async deleteRewrite(vhostName, id) {
        return this.deleteRewriteContent(vhostName, id);
    }

}

export default new HostSharedService();
